import React from "react";
import {
  List,
  DatagridConfigurable,
  TextField,
  TopToolbar,
  SelectColumnsButton,
  ExportButton,
  useDefaultTitle,
  useListContext,
  RaRecord,
  Identifier,
  FunctionField,
  useTranslate,
} from "react-admin";
import { Chip, Avatar, Box, Button } from "@mui/material";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import AddIcon from "@mui/icons-material/Add";
import { Link } from "react-router-dom";

export interface ShowRecord extends RaRecord {
  title?: string;
  episodes?: any[];
}

const ListActions = () => {
  const translate = useTranslate();
  return (
    <TopToolbar>
      <SelectColumnsButton />
      <ExportButton />
      <Button
        component={Link}
        to="/shows/create"
        variant="contained"
        size="small"
        startIcon={<AddIcon />}
      >
        {translate('showsPage.createShow')}
      </Button>
    </TopToolbar>
  );
};

const ShowsTitle = () => {
  const title = useDefaultTitle();
  const { defaultTitle } = useListContext();
  return (
    <>
      <title>{`${title} - ${defaultTitle}`}</title>
      <span>{defaultTitle}</span>
    </>
  );
};

// Custom thumbnail field
const ThumbnailField = ({ record }: { record?: any }) => {
  if (!record) return null;

  // Try to get thumbnail from first episode or from assets
  const thumbUrl = record.episodes?.[0]?.thumb?.url || record.assets?.[0]?.url;

  if (!thumbUrl) {
    return (
      <Avatar sx={{ bgcolor: "primary.main" }}>
        <VideoLibraryIcon />
      </Avatar>
    );
  }

  return (
    <Avatar
      src={thumbUrl}
      variant="rounded"
      sx={{ width: 56, height: 56 }}
    />
  );
};

// Custom episodes count field
const EpisodesCountField = ({ record }: { record?: any }) => {
  const translate = useTranslate();
  if (!record) return null;
  const count = record.episodes?.length || 0;
  return (
    <Chip
      label={`${count} ${translate('showsPage.episode')}${count !== 1 ? "s" : ""}`}
      size="small"
      color="primary"
      variant="outlined"
    />
  );
};

const EmptyList = () => {
  const translate = useTranslate();
  return (
    <Box textAlign="center" p={4}>
      <VideoLibraryIcon sx={{ fontSize: 60, color: "text.secondary", mb: 2 }} />
      <Box mb={2}>{translate('showsPage.noShowsYet')}</Box>
      <Button
        component={Link}
        to="/shows/create"
        variant="contained"
        startIcon={<AddIcon />}
      >
        {translate('showsPage.createShow')}
      </Button>
    </Box>
  );
};

const ShowsList = () => {
  const translate = useTranslate();
  return (
    <List
      resource="sync/override/shows"
      perPage={25}
      sort={{ field: "id", order: "DESC" }}
      actions={<ListActions />}
      filters={[]}
      title={<ShowsTitle />}
      empty={<EmptyList />}
    >
      <DatagridConfigurable
        rowClick={(id: Identifier, _basePath: string, _record?: RaRecord) =>
          `/shows/${id}/edit`
        }
        bulkActionButtons={false}
      >
        <FunctionField
          label={translate('resources.shows.fields.thumbnail')}
          render={(record: any) => <ThumbnailField record={record} />}
        />
        <TextField source="id" label="ID" />
        <TextField source="title" label={translate('resources.shows.fields.title')} />
        <FunctionField
          label={translate('resources.shows.fields.episodes')}
          render={(record: any) => <EpisodesCountField record={record} />}
        />
        <TextField source="description" label={translate('resources.shows.fields.description')} />
      </DatagridConfigurable>
    </List>
  );
};

export default ShowsList;
